import type { PublicPlayerDealResponsePagedData } from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import type { AxiosError } from 'axios';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { NilDealTracker } from 'components/Nil/DealTracker/NilDealTracker';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';

interface NilDealsPageProps extends IGenericPageProps {
  nilDeals: PublicPlayerDealResponsePagedData;
}

const CollectiveGroupDealsPage = ({
  nilDeals,
  ...props
}: NilDealsPageProps) => {
  const router = useRouter();
  const collectiveGroup = router.query.collectiveGroup as string;
  const collectiveKey = collectiveGroup?.toString().split('-').pop();
  const dashesRemoved = collectiveGroup?.replace(/-/g, ' ');
  const collectiveName = dashesRemoved
    .replace(`${collectiveKey}`, '')
    ?.toUpperCase();

  const collectiveGroupName =
    (nilDeals?.list?.length && nilDeals?.list[0]?.collectiveGroup?.name) ||
    collectiveName;

  const title = `${collectiveGroupName} NIL Deal Tracker`;
  const description = `A list of NIL deals from athletes that are a part of ${collectiveGroupName}. The On3 NIL Deal Tracker lists all NIL Deals across the NCAA for college and high school athletes being reported by players, collectives, agents and media. On3 does NOT disclose deal financial terms. Financial data is undisclosed and private to the athlete. `;

  return (
    <PageWrapper {...props} description={description} title={title}>
      <DatabaseLayout>
        <NilDealTracker deals={nilDeals} title={title} />
      </DatabaseLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const nilSiteKey = 368;

  const siteData = await withValidUrl.isValid(ctx, nilSiteKey);
  const userData = await withAuthentication.isAuth(ctx);
  const { collectiveGroup } = ctx.query;
  const collectiveKey = collectiveGroup?.toString().split('-').pop();

  if (!collectiveKey) {
    return {
      notFound: true,
    };
  }

  try {
    const { page = 1 } = ctx.query;
    const params = {
      page,
    };
    const [nilDeals] = await Promise.allSettled([
      proxyApi
        .get<PublicPlayerDealResponsePagedData>(
          `/public/rdb/v1/collective-groups/${collectiveKey}/deals`,
          {
            params,
          },
        )
        .then((r) => r.data),
    ]);

    return {
      props: {
        pageType: 'database',
        siteData: siteData || [],
        userData: userData || {},
        nilDeals: nilDeals.status === 'fulfilled' ? nilDeals.value : {},
      },
    };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.log(err.message);

    return { props: {} };
  }
};

export default withValidUrl(CollectiveGroupDealsPage);
